import { closeModalFunction, URL, closeModalIconTestScoreGuidance, closeModalIcon } from "../dictionary"
import axios from "axios"
import { getAuth, useAuth } from "../../modules/auth"
import { TestScoreStrategyReactGAHandlers } from "../ReactGAHandlers"
import { handleUpdateUserProfile, handleUpdateUserData } from "../../pages/UserProfile"
import { useState } from "react"
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'

let apiToken: any = undefined

if (getAuth()?.api_token && getAuth()?.groups) {
  apiToken = getAuth()?.api_token
}

const EditScoreModal = ({ user, setUser }) => {

  const [loading, setLoading] = useState(false)

  const formSchema = Yup.object().shape({
    satVerbal: Yup.number()
      .nullable()
      .min(200, 'Score cannot be lower than 200')
      .max(800, 'Score cannot exceed 800'),
    satMath: Yup.number()
      .nullable()
      .min(200, 'Score cannot be lower than 200')
      .max(800, 'Score cannot exceed 800'),
    // satComposite: Yup.number()
    //   .nullable()
    //   .min(400, 'Score cannot be lower than 400')
    //   .max(1600, 'Score cannot exceed 1600'),
    actComposite: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    actReading: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    actMath: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    actEnglish: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    actScience: Yup.number()
      .nullable()
      .min(1, 'Score cannot be lower than 1')
      .max(36, 'Score cannot exceed 36'),
    actWriting: Yup.number()
      .nullable()
      .min(2, 'Score cannot be lower than 2')
      .max(12, 'Score cannot exceed 12'),
    gpaUnweighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)')
      .max(4.0, 'GPA based on a 4.0 scale (enter unweighted value between 0.00 - 4.00)'),
    gpaWeighted: Yup.number()
      .nullable()
      .min(0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)')
      .max(5.0, 'GPA based on a 4.0 scale (enter weighted value between 0.00 - 5.00)')
  })

  let initialValues = {
    satVerbal: user?.satVerbal,
    satMath: user?.satMath,
    actComposite: user?.actComposite,
    actReading: user?.actReading,
    actMath: user?.actMath,
    actEnglish: user?.actEnglish,
    actScience: user?.actScience,
    actWriting: user?.actWriting,
    gpaUnweighted: user?.gpaUnweighted,
    gpaWeighted: user?.gpaWeighted
  }

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: formSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)

      const cleanedValues = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [key, value === "" ? null : value])
      );

      if (cleanedValues.satVerbal == null && cleanedValues.satMath == null) {
        cleanedValues.satComposite = null
      } else {
        cleanedValues.satComposite = cleanedValues.satVerbal + cleanedValues.satMath
      }

      try {
        await handleUpdateUserData(user, cleanedValues, setUser, apiToken);
        await handleUpdateUserProfile(setUser, apiToken)
      } catch (error) {
        console.error(error)
        alert('Error encountered')
        setStatus('The test score details are incorrect')
        setSubmitting(false)
      } finally {
        setLoading(false)
      }
    }
  })


  const {
    handleGAEventEditScoreProfile,
    handleGAEventOverlayButton
  } = TestScoreStrategyReactGAHandlers()

  const returnInputBlock = ({ title, name, min, max, act2 = false }) => {
    return (
      <div className="d-flex flex-column mb-3">
        <label className={`form-label dca-testscore-editscore-field-title${act2 ? 2 : ''}`}>
          {title}
        </label>
        <div className="d-flex flex-row">
          <input
            type="number"
            min={min}
            max={max}
            {...formik.getFieldProps(name)}
            className={clsx(
              "form-control dca-testscore-editscore-input",
              {
                'is-invalid': formik.touched[name] && formik.errors[name],
                'is-valid': formik.touched[name] && !formik.errors[name],
              }
            )}
          />
          <i
            className="bi bi-x-circle align-self-center"
            style={{ cursor: 'pointer', marginLeft: '5px' }}
            onClick={() => {
              formik.setFieldValue(name, "");
              formik.setFieldTouched(name, false)
            }}
          />
        </div>
        {formik.touched[name] && formik.errors[name] && (
          <div className="invalid-feedback d-flex dca-editscore-modal-error-text">
            {formik.errors[name]}
          </div>
        )}
      </div>
    )
  }

  const gpa = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>Grade Point Average (GPA)</p>
      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Unweighted GPA', name: 'gpaUnweighted', min: 0, max: 4.0 })}

        {returnInputBlock({ title: 'Weighted GPA', name: 'gpaWeighted', min: 0, max: 5.0 })}
      </div>

    </div>
  )

  const satScores = (
    <div>
      <p className='dca-testscore-editscore-sat-section-title'>SAT Scores</p>
      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Best SAT Reading Score', name: 'satVerbal', min: 200, max: 800 })}

        {returnInputBlock({ title: 'Best SAT Math Score', name: 'satMath', min: 200, max: 800 })}
      </div>

    </div>
  )

  const actScores = (
    <div>
      <p className='dca-testscore-editscore-act-section-title'>ACT Scores</p>

      <div className="dca-editscoreprofile-modal-score-wrapper">
        {returnInputBlock({ title: 'Best ACT Composite Score', name: 'actComposite', min: 1, max: 36 })}

        {returnInputBlock({ title: 'Best ACT Reading Score', name: 'actReading', min: 1, max: 36 })}

      </div>

      <div className="dca-editscoreprofile-modal-score-wrapper">

        {returnInputBlock({ title: 'Best ACT Math Score', name: 'actMath', min: 1, max: 36, act2: true })}

        {returnInputBlock({ title: 'Best ACT English Score', name: 'actEnglish', min: 1, max: 36, act2: true })}

      </div>

      <div className="dca-editscoreprofile-modal-score-wrapper">

        {returnInputBlock({ title: 'Best ACT Science Score', name: 'actScience', min: 1, max: 36, act2: true })}

        {returnInputBlock({ title: 'Best ACT Writing Score', name: 'actWriting', min: 2, max: 12, act2: true })}
      </div>

    </div>
  )

  return (<div className="modal fade" id="editScoreProfileScattergram">
    <div className="modal-dialog modal-dialog modal-dialog-centered">
      <div className="modal-content dca-editscore-profile-modal-scattergram">

        <div className="modal-body fw-bold d-flex flex-column">
          <p className="dca-testscore-editscore-title">Enter Your Profile Details</p>

          <form
            onSubmit={formik.handleSubmit}
            noValidate
            id="testscoresForm">
            {/* {closeModalIconTestScoreGuidance(handleGAEventOverlayButton)} */}
            {closeModalIcon('editscore-profile-scattergram')}

            <div className='d-flex flex-column justify-content-center' style={{ width: '100%' }}>
              {gpa}
              {satScores}
              {actScores}
            </div>

            {/* Original */}
            <div className="d-flex flex-row justify-content-center" style={{ marginTop: '45px' }}>
              <button
                type="button"
                className="btn btn-light dca-testscore-editscore-discard-bttn"
                data-bs-dismiss="modal"
                id="closeModalIcon"
                // onClick={() => handleGAEventOverlayButton("Discard")}
                style={{ marginRight: '12px' }}
              >
                Discard
              </button>
              <button
                type="submit"
                className="rounded dca-testscore-editscore-submit-bttn"
                disabled={!formik.isValid || formik.isSubmitting}
                data-bs-dismiss="modal"
                style={{ marginLeft: '12px' }}
              >
                Submit
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  </div>)
}

export { EditScoreModal }